import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Award, Plus, RefreshCw, AlertCircle, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import SponsorsFilters from './sponsors/SponsorsFilters';
import SponsorsList from './sponsors/SponsorsList';
import SponsorFormModal from './sponsors/SponsorFormModal';
import { blankSponsor } from './sponsors/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SponsorsTab = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | sponsor object being edited
  const [statusMsg, setStatusMsg] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');

  const fetchSponsors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sponsors`);
      const data = await res.json();
      setSponsors(data.sponsors || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Sponsors fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSponsors(); }, [fetchSponsors]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sponsors.filter((s) => {
      if (tierFilter !== 'all' && s.tier !== tierFilter) return false;
      if (q) {
        const hay = `${s.business_name} ${s.contact_name} ${s.email} ${s.notes}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sponsors, searchQuery, tierFilter]);

  const totalRaised = useMemo(
    () => sponsors.filter((s) => s.status === 'active').reduce((sum, s) => sum + (Number(s.amount) || 0), 0),
    [sponsors]
  );

  // Renewals coming up within 60 days
  const upcomingRenewals = useMemo(() => {
    const now = Date.now();
    const cutoff = now + 60 * 24 * 60 * 60 * 1000;
    return sponsors.filter((s) => {
      if (!s.renewal_date) return false;
      const t = new Date(s.renewal_date).getTime();
      if (Number.isNaN(t)) return false;
      return t >= now && t <= cutoff;
    });
  }, [sponsors]);

  const saveSponsor = async () => {
    if (!editing || !editing.business_name?.trim()) {
      setStatusMsg({ type: 'error', text: 'Business name is required.' });
      return;
    }
    const isNew = !editing.id;
    const url = isNew ? `${API_URL}/api/sponsors` : `${API_URL}/api/sponsors/${editing.id}`;
    const method = isNew ? 'POST' : 'PATCH';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save sponsor');
      setStatusMsg({ type: 'success', text: isNew ? 'Sponsor added.' : 'Sponsor updated.' });
      setEditing(null);
      fetchSponsors();
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.message });
    }
  };

  const deleteSponsor = async (sponsor) => {
    if (!window.confirm(`Delete sponsor "${sponsor.business_name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/sponsors/${sponsor.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to delete sponsor');
      setSponsors((prev) => prev.filter((s) => s.id !== sponsor.id));
      setStatusMsg({ type: 'success', text: 'Sponsor deleted.' });
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.message });
    }
  };

  const startNew = () => { setEditing({ ...blankSponsor() }); setStatusMsg(null); };
  const startEdit = (s) => setEditing({ ...s });

  return (
    <>
      {/* Stats */}
      <div className="stats-grid">
        <Card className="stat-card total-card">
          <CardContent>
            <div className="stat-content">
              <Award className="stat-icon" size={32} />
              <div>
                <div className="stat-value">{sponsors.length}</div>
                <div className="stat-label">Total Sponsors</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card age-card">
          <CardContent>
            <div className="stat-content">
              <DollarSign className="stat-icon" size={32} style={{ color: '#10b981' }} />
              <div>
                <div className="stat-value">${totalRaised.toLocaleString()}</div>
                <div className="stat-label">Active Sponsorships</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card age-card">
          <CardContent>
            <div className="stat-content">
              <AlertCircle className="stat-icon" size={32} style={{ color: '#f59e0b' }} />
              <div>
                <div className="stat-value">{upcomingRenewals.length}</div>
                <div className="stat-label">Renewals Due (60d)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status banner */}
      {statusMsg && (
        <div
          className={`email-status-banner ${statusMsg.type}`}
          data-testid="sponsor-status-banner"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            background: statusMsg.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${statusMsg.type === 'success' ? '#10b981' : '#ef4444'}`,
            color: '#fff',
          }}
        >
          {statusMsg.text}
        </div>
      )}

      {/* Renewal alerts */}
      {upcomingRenewals.length > 0 && (
        <Card style={{ marginBottom: '1rem', borderColor: 'rgba(245,158,11,0.5)' }}>
          <CardContent>
            <h3 style={{ color: '#f59e0b', margin: '0 0 0.5rem 0' }}>⚠ Renewal Reminders</h3>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#fff' }}>
              {upcomingRenewals.map((s) => (
                <li key={s.id}>
                  <strong>{s.business_name}</strong>{' '}— renews {new Date(s.renewal_date).toLocaleDateString()}
                  {s.email && (
                    <> &nbsp;•&nbsp; <a href={`mailto:${s.email}`} style={{ color: '#FFD700' }}>{s.email}</a></>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="actions-bar">
        <Button onClick={startNew} className="action-btn refresh-btn" data-testid="add-sponsor-btn">
          <Plus size={18} /> Add Sponsor
        </Button>
        <Button onClick={fetchSponsors} className="action-btn refresh-btn" disabled={loading}>
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      <SponsorsFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        tierFilter={tierFilter}
        setTierFilter={setTierFilter}
        filteredCount={filtered.length}
        totalCount={sponsors.length}
      />

      {editing && (
        <SponsorFormModal
          sponsor={editing}
          onChange={setEditing}
          onSave={saveSponsor}
          onCancel={() => setEditing(null)}
        />
      )}

      <SponsorsList
        loading={loading}
        sponsors={sponsors}
        filtered={filtered}
        onEdit={startEdit}
        onDelete={deleteSponsor}
      />
    </>
  );
};

export default SponsorsTab;
